import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Users, Mail, X, LogOut, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function FamilyPage() {
  const { user, checkSession } = useAuth();
  const [data, setData] = useState({ family: null, members: [], invites: [] });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptToken, setAcceptToken] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/family/me");
      setData(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    // Pick up an invite token from URL (e.g. /family-workspace?token=...)
    const url = new URL(window.location.href);
    const tok = url.searchParams.get("token");
    if (tok) {
      setAcceptToken(tok);
    }
  }, []);

  const accept = async () => {
    if (!acceptToken) return;
    setLoading(true);
    try {
      await api.post("/family/accept", { token: acceptToken });
      toast.success("Joined the family workspace");
      setAcceptToken("");
      window.history.replaceState(null, "", "/family-workspace");
      await checkSession();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not accept invite");
    } finally {
      setLoading(false);
    }
  };

  const invite = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/family/invite", { email: email.trim() });
      toast.success("Invite sent");
      setEmail("");
      await load();
      await checkSession();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Invite failed");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/family/invite/${id}`);
      toast.success("Invite revoked");
      load();
    } catch {
      toast.error("Could not revoke");
    }
  };

  const leave = async () => {
    if (!window.confirm("Leave this family workspace? Your tasks will become personal again.")) return;
    try {
      await api.post("/family/leave");
      toast.success("Left family workspace");
      await checkSession();
      load();
    } catch {
      toast.error("Could not leave");
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/family-workspace?token=${token}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Invite link copied"));
  };

  const isOwner = data?.family?.owner_id === user?.id;

  return (
    <div className="px-4 sm:px-8 py-8 max-w-3xl mx-auto">
      <div className="mb-7 anim-fade-up">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Shared workspace</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Family</h1>
        <p className="text-sm text-muted-foreground mt-2">Invite household members to share the Family dashboard. Everyone can add, edit and complete shared tasks.</p>
      </div>

      {acceptToken && (
        <div data-testid="family-accept-banner" className="rounded-xl border border-primary/40 bg-primary/5 p-5 mb-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-primary mt-1" size={18} />
            <div className="flex-1">
              <div className="font-display font-bold">Pending invite</div>
              <p className="text-sm text-muted-foreground mt-0.5">You have an invitation to join a family workspace.</p>
            </div>
            <Button onClick={accept} disabled={loading} data-testid="family-accept-btn">
              {loading ? "Joining…" : "Accept invite"}
            </Button>
          </div>
        </div>
      )}

      {/* Invite */}
      <section className="rounded-xl border border-border bg-card p-5 mb-5">
        <h2 className="font-display font-bold mb-1 text-sm uppercase tracking-wider">Invite a member</h2>
        <p className="text-xs text-muted-foreground mb-4">
          They'll receive an email with a one-tap link to join your Family dashboard.
        </p>
        <form className="flex gap-2" onSubmit={invite}>
          <div className="relative flex-1">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="family-invite-email"
              type="email"
              placeholder="member@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9 h-11"
              required
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="family-invite-btn" className="h-11">
            Send invite
          </Button>
        </form>
      </section>

      {/* Members */}
      <section className="rounded-xl border border-border bg-card p-5 mb-5">
        <h2 className="font-display font-bold mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
          <Users size={14} /> Members ({data.members?.length || 0})
        </h2>
        {data.members?.length === 0 ? (
          <p className="text-xs text-muted-foreground">No members yet — invite someone to get started.</p>
        ) : (
          <ul className="space-y-2">
            {data.members.map((m) => (
              <li
                key={m.id}
                data-testid={`family-member-${m.id}`}
                className="flex items-center justify-between bg-secondary/40 rounded-md px-3 py-2.5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-sm shrink-0">
                    {(m.name || m.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  </div>
                </div>
                {data.family?.owner_id === m.id && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 rounded px-2 py-0.5">
                    Owner
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      {data.invites?.length > 0 && (
        <section className="rounded-xl border border-border bg-card p-5 mb-5">
          <h2 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">
            Pending invites ({data.invites.length})
          </h2>
          <ul className="space-y-2">
            {data.invites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between bg-secondary/40 rounded-md px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{inv.email}</div>
                  <div className="text-xs text-muted-foreground">Sent {new Date(inv.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => copyLink(inv.token)} data-testid={`family-copy-${inv.id}`}>
                    <Copy size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => revoke(inv.id)} data-testid={`family-revoke-${inv.id}`}>
                    <X size={13} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Leave */}
      {data.family && (
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display font-bold mb-2 text-sm uppercase tracking-wider">{isOwner ? "Disband family" : "Leave family"}</h2>
          <p className="text-xs text-muted-foreground mb-4">
            {isOwner
              ? "As owner, leaving will disband the workspace for all members. Their personal tasks remain safe."
              : "You can re-join any time with a new invite."}
          </p>
          <Button variant="destructive" onClick={leave} data-testid="family-leave-btn">
            <LogOut size={14} className="mr-1.5" /> {isOwner ? "Disband family" : "Leave"}
          </Button>
        </section>
      )}
    </div>
  );
}
